/**
 * 発注一覧データ取得フック
 *
 * 設計書 05-frontend-design.md §5.1 整合。
 * `filter` / `pagination` の状態管理と `getPurchaseOrders` API 呼出を集約し、
 * `PurchaseOrderListPage.tsx` のファイル肥大化(設計書 09 PR-10 レビュー観点)を回避する。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@/shared/lib/logger';
import { getPurchaseOrders } from '../services/purchaseOrderApiService';
import { PURCHASE_ORDER_DEFAULTS } from '../types/purchaseOrder';
import type {
  ApplyFilterInput,
  PurchaseOrderListQuery,
  PurchaseOrderListResult,
  PurchaseOrderPagination,
  PurchaseOrderViewModel,
} from '../types/purchaseOrder';

/**
 * 初回 API 応答到達前に表示する空ページネーション
 *
 * `totalPages: 1` は antd Pagination の最小要件(0 不可)に整合。
 */
const INITIAL_PAGINATION_BEFORE_FETCH: PurchaseOrderPagination = {
  currentPage: PURCHASE_ORDER_DEFAULTS.FIRST_PAGE,
  totalPages: 1,
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

interface UsePurchaseOrdersResult {
  readonly items: readonly PurchaseOrderViewModel[];
  readonly pagination: PurchaseOrderPagination;
  /** 現在の filter.limit から導出した1ページあたりの件数(antd Pagination 用) */
  readonly pageSize: number;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly filter: PurchaseOrderListQuery;
  /** フィルタ適用(page は常に 1 ページ目にリセット) */
  readonly setFilter: (params: { readonly filter: ApplyFilterInput }) => void;
  readonly setPage: (params: { readonly page: number }) => void;
  readonly reload: () => Promise<void>;
  /** Alert の closable などユーザー操作で error 表示を消す用途 */
  readonly dismissError: () => void;
}

/**
 * 発注一覧の filter/pagination state を管理し、API 呼出を行うカスタムフック
 *
 * @param params.initialFilter 省略時は `{ page: 1, limit: PAGE_SIZE }` で初期化
 */
export function usePurchaseOrders(params?: {
  readonly initialFilter?: PurchaseOrderListQuery;
}): UsePurchaseOrdersResult {
  const [filter, setFilterState] = useState<PurchaseOrderListQuery>(
    params?.initialFilter ?? {
      page: PURCHASE_ORDER_DEFAULTS.FIRST_PAGE,
      limit: PURCHASE_ORDER_DEFAULTS.PAGE_SIZE,
    }
  );
  const [items, setItems] = useState<readonly PurchaseOrderViewModel[]>([]);
  const [pagination, setPagination] = useState<PurchaseOrderPagination>(
    INITIAL_PAGINATION_BEFORE_FETCH
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 最新リクエストの世代カウンタ。
   * filter 高速変更時の race condition で古い応答が新しい応答を上書きするのを防ぐ。
   */
  const latestRequestIdRef = useRef<number>(0);
  /** アンマウント後の setState を防ぐためのマウント状態フラグ */
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchOrders = useCallback(
    async (fetchParams: {
      readonly currentFilter: PurchaseOrderListQuery;
    }): Promise<void> => {
      latestRequestIdRef.current += 1;
      const requestId = latestRequestIdRef.current;
      const isLatestAndMounted = (): boolean =>
        isMountedRef.current && requestId === latestRequestIdRef.current;

      setIsLoading(true);
      setError(null);
      try {
        const result: PurchaseOrderListResult = await getPurchaseOrders({
          query: fetchParams.currentFilter,
        });
        if (!isLatestAndMounted()) {
          return;
        }
        setItems(result.items);
        setPagination(result.pagination);
      } catch (caughtError) {
        if (!isLatestAndMounted()) {
          return;
        }
        const errorInstance =
          caughtError instanceof Error
            ? caughtError
            : new Error('発注一覧の取得に失敗しました');
        setError(errorInstance);
        logger.captureError(caughtError, 'usePurchaseOrders.fetchOrders');
      } finally {
        if (isLatestAndMounted()) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    void fetchOrders({ currentFilter: filter });
  }, [filter, fetchOrders]);

  const setFilter = useCallback(
    (updateParams: { readonly filter: ApplyFilterInput }): void => {
      // フィルタ変更時は1ページ目に戻す(page を必ず FIRST_PAGE で上書き)
      setFilterState({
        ...updateParams.filter,
        page: PURCHASE_ORDER_DEFAULTS.FIRST_PAGE,
      });
    },
    []
  );

  const setPage = useCallback((updateParams: { readonly page: number }): void => {
    setFilterState((prev) => ({ ...prev, page: updateParams.page }));
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    await fetchOrders({ currentFilter: filter });
  }, [fetchOrders, filter]);

  const dismissError = useCallback((): void => {
    setError(null);
  }, []);

  const pageSize = filter.limit ?? PURCHASE_ORDER_DEFAULTS.PAGE_SIZE;

  return {
    items,
    pagination,
    pageSize,
    isLoading,
    error,
    filter,
    setFilter,
    setPage,
    reload,
    dismissError,
  };
}
